import { useId, useState } from "react"
import designSystemStyles from "@shared/styles/designSystemStyles"
import styles from "@shared/styles/components/LeftMenuSection.module.css"
import { SystemIcon } from "./SystemIcon"

export interface LeftMenuSectionProps {
    title: string
    children: React.ReactNode
    /** Shown as a muted badge only while the section is closed. */
    hiddenCount?: number
    /** Initial state when uncontrolled. Defaults to open. */
    defaultOpen?: boolean
    /** Controlled mode; wins over internal state when defined. */
    isOpen?: boolean
    onToggle?: (nextOpen: boolean) => void
    className?: string
}

export const LeftMenuSection: React.FC<LeftMenuSectionProps> = ({
    title,
    children,
    hiddenCount,
    defaultOpen = true,
    isOpen,
    onToggle,
    className
}) => {
    const [internalOpen, setInternalOpen] = useState(defaultOpen)
    const open = isOpen ?? internalOpen
    const headerId = useId()
    const contentId = useId()

    const sectionClassName = [
        "DSLeftMenuSection",
        styles.section,
        open ? styles.open : "",
        className
    ].filter(Boolean).join(" ")

    const toggle = () => {
        const nextOpen = !open
        if (isOpen === undefined) {
            setInternalOpen(nextOpen)
        }
        onToggle?.(nextOpen)
    }

    const showCount = !open && typeof hiddenCount === "number" && hiddenCount > 0

    return (
        <div className={sectionClassName}>
            <button
                type="button"
                id={headerId}
                className={styles.header}
                aria-expanded={open}
                aria-controls={contentId}
                onClick={toggle}
            >
                <span className={styles.title}>{title}</span>
                {showCount && <span className={styles.count}>{hiddenCount}</span>}
                <span className={styles.chevron} aria-hidden="true">
                    <SystemIcon
                        type="chevron"
                        color={designSystemStyles.colorTextSecondary}
                        size="small"
                    />
                </span>
            </button>
            <div
                id={contentId}
                role="region"
                aria-labelledby={headerId}
                className={styles.content}
                hidden={!open}
            >
                {children}
            </div>
        </div>
    )
}
